import React, { useState, useEffect } from 'react'
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Snackbar,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { 
  Error as ErrorIcon, 
  TrendingUp, 
  TrendingDown,
  Equalizer,
  AttachMoney,
  ShoppingCart,
  People
} from '@material-ui/icons'
import CountUp from 'react-countup'
import BilanService from '../../services/BilanService'

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.shadows[4]
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    backgroundColor: '#ffebee',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #f44336'
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    color: '#f44336'
  },
  positive: {
    color: '#4CAF50',
    fontWeight: 'bold'
  },
  negative: {
    color: '#f44336',
    fontWeight: 'bold'
  },
  statCard: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2)
  },
  productTable: {
    marginTop: theme.spacing(2)
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& th': {
      color: 'white',
      fontWeight: 'bold'
    }
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  select: {
    marginBottom: theme.spacing(2),
    minWidth: 200
  },
  clientCard: {
    borderLeft: '4px solid #FFD700',
    background: 'linear-gradient(135deg, #fff9c4 0%, #fffde7 100%)'
  },
  topClient: {
    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
    border: '2px solid #ff9800'
  },
  avatar: {
    backgroundColor: '#ff9800',
    width: theme.spacing(6),
    height: theme.spacing(6)
  },
  rankBadge: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: '#ff5722',
    color: 'white',
    borderRadius: '50%',
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.8rem'
  },
  montant: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2E7D32'
  }
}));

function Bilan() {
  const classes = useStyles();
  const [bilanData, setBilanData] = useState(null);
  const [quotidienData, setQuotidienData] = useState(null);
  const [bonsClients, setBonsClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [periode, setPeriode] = useState('jour');
  const [typeBilan, setTypeBilan] = useState('periode');

  const periodes = [
    { key: 'jour', label: "Aujourd'hui" },
    { key: 'mois', label: 'Mois en cours' },
    { key: 'annee', label: 'Année en cours' }
  ];

  useEffect(() => {
    fetchData();
  }, [periode, typeBilan]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (typeBilan === 'quotidien') {
        console.log('🔄 Chargement du bilan quotidien...');
        const response = await BilanService.getBilanQuotidienAuto();
        setQuotidienData(response.data);
        setBilanData(null);
      } else {
        console.log(`🔄 Chargement du bilan pour la période: ${periode}`);
        const response = await BilanService.getBenefices(periode);
        setBilanData(response.data);
        setQuotidienData(null);
      }

    } catch (error) {
      console.error('❌ Erreur lors du chargement du bilan:', error);
      setError('Erreur lors du chargement du bilan');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Nouvelle fonction de formatage basée sur CountUp
  const FormattedMontant = ({ montant, className = '' }) => {
    return (
      <CountUp 
        start={0} 
        end={montant || 0} 
        duration={1.5} 
        separator=" " 
        suffix=" F CFA"
        className={className}
      />
    );
  };

  const formatPourcentage = (valeur) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valeur || 0);
  };

  const getTrendIcon = (evolution) => {
    if (evolution.includes('+')) {
      return <TrendingUp className={classes.positive} />;
    } else if (evolution.includes('-')) {
      return <TrendingDown className={classes.negative} />;
    }
    return <Equalizer />;
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleRetry = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" gutterBottom style={{ marginBottom: '30px', color: 'gray' }}>
        📊 Bilan Financier
      </Typography>

      {/* Sélecteurs */}
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        <Grid item>
          <FormControl className={classes.select}>
            <InputLabel>Type de bilan</InputLabel>
            <Select
              value={typeBilan}
              onChange={(e) => setTypeBilan(e.target.value)}
            >
              <MenuItem value="periode">Bilan par période</MenuItem>
              <MenuItem value="quotidien">Bilan quotidien auto</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        {typeBilan === 'periode' && (
          <Grid item>
            <FormControl className={classes.select}>
              <InputLabel>Période</InputLabel>
              <Select
                value={periode}
                onChange={(e) => setPeriode(e.target.value)}
              >
                {periodes.map((p) => (
                  <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {error && (
        <div className={classes.errorContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="body1" style={{ flex: 1 }}>
            {error}
          </Typography>
          <Button color="secondary" onClick={handleRetry}>
            Réessayer
          </Button>
        </div>
      )}

      {/* Bilan Quotidien Auto */}
      {typeBilan === 'quotidien' && quotidienData && (
        <Grid container spacing={3}>
          {/* Évolution */}
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📈 Évolution quotidienne
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <div style={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Chiffre d'affaires
                      </Typography>
                      <Typography variant="h6" className={classes.positive}>
                        <FormattedMontant montant={quotidienData.aujourdhui.chiffre_affaires.totalVentes} />
                      </Typography>
                      <Chip
                        icon={getTrendIcon(quotidienData.evolution.chiffre_affaires)}
                        label={quotidienData.evolution.chiffre_affaires}
                        className={classes.chip}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <div style={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Bénéfice brut
                      </Typography>
                      <Typography variant="h6" className={classes.positive}>
                        <FormattedMontant montant={quotidienData.aujourdhui.benefice_brut} />
                      </Typography>
                      <Chip
                        icon={getTrendIcon(quotidienData.evolution.benefice_brut)}
                        label={quotidienData.evolution.benefice_brut}
                        className={classes.chip}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <div style={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Nombre de ventes
                      </Typography>
                      <Typography variant="h6">
                        {quotidienData.aujourdhui.chiffre_affaires.nombreVentes}
                      </Typography>
                      <Chip
                        icon={getTrendIcon(quotidienData.evolution.nombre_ventes)}
                        label={quotidienData.evolution.nombre_ventes}
                        className={classes.chip}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Aujourd'hui vs Hier */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📊 Aujourd'hui
                </Typography>
                {renderStatsCard(quotidienData.aujourdhui)}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📅 Hier
                </Typography>
                {renderStatsCard(quotidienData.hier)}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bilan par Période */}
      {typeBilan === 'periode' && bilanData && (
        <Grid container spacing={3}>
          {/* Cartes principales */}
          <Grid item xs={12} md={4}>
            <Card className={classes.statCard}>
              <CardContent>
                <AttachMoney style={{ fontSize: 40, color: '#4CAF50', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Chiffre d'affaires
                </Typography>
                <Typography variant="h4" className={classes.montant}>
                  <FormattedMontant montant={bilanData.chiffre_affaires.totalVentes} />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {bilanData.chiffre_affaires.nombreVentes} ventes
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.statCard}>
              <CardContent>
                <TrendingUp style={{ fontSize: 40, color: '#2196F3', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Bénéfice brut
                </Typography>
                <Typography variant="h4" className={classes.montant}>
                  <FormattedMontant montant={bilanData.benefice_brut} />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Marge: {bilanData.marge_brute}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.statCard}>
              <CardContent>
                <ShoppingCart style={{ fontSize: 40, color: '#FF9800', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Coût des marchandises
                </Typography>
                <Typography variant="h4" className={classes.negative}>
                  <FormattedMontant montant={bilanData.cout_marchandises_vendues.totalCout} />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {bilanData.cout_marchandises_vendues.nombreProduits} produits
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Détails par mode de paiement */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  💳 Chiffre d'affaires par mode de paiement
                </Typography>
                {bilanData.chiffre_affaires.par_mode_paiement.map((item, index) => (
                  <Grid container key={index} justifyContent="space-between" style={{ marginBottom: '10px' }}>
                    <Typography variant="body2">
                      {item.mode_payement || 'Non spécifié'}
                    </Typography>
                    <Typography variant="body2" className={classes.positive}>
                      <FormattedMontant montant={item.totalVentes} />
                    </Typography>
                  </Grid>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Statistiques */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📈 Statistiques
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <People style={{ fontSize: 20, marginRight: '5px' }} />
                    <Typography variant="body2">
                      Vendeurs: {bilanData.statistiques.total_vendeurs}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <People style={{ fontSize: 20, marginRight: '5px' }} />
                    <Typography variant="body2">
                      Clients: {bilanData.statistiques.total_clients}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" style={{ marginTop: '10px' }}>
                      Moyenne/vente: <FormattedMontant montant={bilanData.chiffre_affaires.moyenneParVente} />
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Tableau des bénéfices par produit */}
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🏆 Produits les plus rentables
                </Typography>
                <TableContainer component={Paper} className={classes.productTable}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Produit</TableCell>
                        <TableCell align="right">Quantité vendue</TableCell>
                        <TableCell align="right">CA réalisé</TableCell>
                        <TableCell align="right">Bénéfice</TableCell>
                        <TableCell align="right">Marge</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bilanData.benefices_par_produit.map((produit, index) => (
                        <TableRow key={index}>
                          <TableCell>{produit.nomProd}</TableCell>
                          <TableCell align="right">{produit.quantiteVendue}</TableCell>
                          <TableCell align="right">
                            <FormattedMontant montant={produit.chiffreAffaires} />
                          </TableCell>
                          <TableCell align="right" className={classes.positive}>
                            <FormattedMontant montant={produit.benefice} />
                          </TableCell>
                          <TableCell align="right" className={classes.positive}>
                            {formatPourcentage(produit.marge)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={error}
        action={
          <Button color="inherit" size="small" onClick={handleCloseSnackbar}>
            Fermer
          </Button>
        }
      />
    </div>
  );
}

// Composant pour afficher les statistiques d'une carte
function renderStatsCard(data) {
  const FormattedMontant = ({ montant }) => {
    return (
      <CountUp 
        start={0} 
        end={montant || 0} 
        duration={1.5} 
        separator=" " 
        suffix=" F CFA"
      />
    );
  };

  return (
    <div>
      <Typography variant="body2">
        <strong>Chiffre d'affaires:</strong> <FormattedMontant montant={data.chiffre_affaires.totalVentes} />
      </Typography>
      <Typography variant="body2">
        <strong>Bénéfice brut:</strong> <FormattedMontant montant={data.benefice_brut} />
      </Typography>
      <Typography variant="body2">
        <strong>Nombre de ventes:</strong> {data.chiffre_affaires.nombreVentes}
      </Typography>
      <Typography variant="body2">
        <strong>Quantité vendue:</strong> {data.chiffre_affaires.quantiteVendue}
      </Typography>
    </div>
  );
}

export default Bilan;